import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'

import { useOsApi } from '@/frontend/hooks/api/use-os-api'

/**
 * Fetches the Stripe-hosted invoice URL and opens it in a new tab.
 *
 * Named as an action (`useOpenInvoice`) rather than a data hook (`useGetInvoiceUrl`)
 * because this is a fire-and-forget user interaction, not a cached query — the URL
 * is never reused or displayed in the UI.
 *
 * Implemented as a mutation because the entire purpose is a side effect
 * (open a browser tab) — there’s no data to cache or sync.
 */
export function useOpenStripeInvoice() {
  const api = useOsApi()

  return useMutation({
    mutationFn: async (invoiceId: string) => {
      const response = await api.get('/invoices/:invoiceId/url', { params: { invoiceId } })
      if (!response.success) throw new Error(response.error.message)
      return response.data.url
    },
    onSuccess: (url) => {
      window.open(url, '_blank', 'noopener,noreferrer')
    },
    onError: (err) => {
      toast.error(err.message ?? 'Could not load invoice')
    },
  })
}
